// What a check of corrections says. It only analyses the pages of the elements being checked, so it is not an
// analysis of the website: no estimate, no comparison with another analysis and no «resolved problems». It
// says what was checked and what each check decided, one element at a time.
// On the analysis tab (`compact`) it is a short banner with one next step; the full detail stays on the
// check's own results screen.
import { ArrowRightIcon } from '@radix-ui/react-icons';
import { Link } from '@tanstack/react-router';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import type { CheckedByScan, ScanDetail } from '../api';
import { buttonClass } from '../ui';
import { pathOf } from '../util';
import { useDate } from './scan-parts';
import { StatusTag } from './scan-status';

/** One sentence for the banner: what the check settled, before the next action. */
function checkSummary(t: TFunction, checked: CheckedByScan): string {
  const { verified, persists, pending, items } = checked;
  const pendingItems = items.filter(
    (item) =>
      item.check?.result === 'no-comprobable' || item.check?.result === 'requiere-revision' || !item.check,
  );
  const allPossibleChange =
    pending > 0 &&
    pendingItems.length === pending &&
    pendingItems.every((item) => item.check?.reason === 'posible-cambio');
  if (allPossibleChange && persists === 0 && verified === 0) {
    return t('check.summaryPossibleChange', { count: pending });
  }
  if (pending > 0 && persists === 0 && verified === 0) {
    return t('check.summaryPending', { count: pending });
  }
  if (persists > 0 && pending === 0 && verified === 0) {
    return t('check.summaryPersists', { count: persists });
  }
  if (pending === 0 && persists === 0) return t('check.summaryVerified');
  return t('check.summaryMixed', { verified, persists, pending });
}

function CheckItems({ items }: { items: CheckedByScan['items'] }) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <ul className="check-items">
      {items.map((item) => (
        <li key={item.id}>
          <p className="font-bold">
            {item.element.title}
            {item.element.zoneLabel && (
              <span className="text-ink-muted font-normal"> — {item.element.zoneLabel}</span>
            )}
          </p>
          <p className="text-sm">{item.ruleTitle}</p>
          <p className="text-sm text-ink-muted wrap-anywhere">
            {t('follow.tab.page')}: {pathOf(item.page.url)} ·{' '}
            {item.devices.map((device) => t(`scan.devices.${device}`)).join(', ')}
          </p>
          <p className="text-sm">
            <strong>
              {item.check ? t(`follow.check.results.${item.check.result}`) : t('follow.check.none')}
            </strong>
            {item.check && <span className="block">{t(`follow.check.reasons.${item.check.reason}`)}</span>}
          </p>
        </li>
      ))}
    </ul>
  );
}

export function CheckOutcome({
  scan,
  siteId,
  headingLevel = 3,
  seeResults = false,
  compact = false,
}: {
  scan: ScanDetail;
  siteId: string;
  headingLevel?: 3 | 4;
  /** Offers the way to the results of the pages that were checked (not needed where they are on screen). */
  seeResults?: boolean;
  /** Short banner for the analysis tab: one next step, detail folded. */
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const date = useDate();
  const checked = scan.checked;
  const Heading = `h${headingLevel}` as 'h3' | 'h4';
  const headingId = `comprobacion-${scan.id}`;
  const needsHuman = Boolean(checked && checked.pending > 0);
  const stillFails = Boolean(checked && checked.persists > 0);
  const primaryToFollow = needsHuman || stillFails;
  const primaryLabel = needsHuman
    ? t('check.nextPending')
    : stillFails
      ? t('check.nextPersists')
      : t('check.nextVerified');

  if (compact) {
    return (
      <aside className="check-banner" aria-labelledby={headingId}>
        <header className="check-banner-head">
          <div className="check-banner-title">
            <Heading id={headingId}>{t('check.title')}</Heading>
            <p className="check-banner-note">{t('check.titleNote')}</p>
            <p className="latest-meta">
              <span>
                {t('scans.meta', { date: date(scan.createdAt), type: t(`scans.types.${scan.type}`) })}
              </span>
              <StatusTag outcome={scan.outcome} />
            </p>
          </div>
          <div className="check-actions">
            {primaryToFollow ? (
              <Link to="/sites/$siteId/seguimiento" params={{ siteId }} className={buttonClass('primary')}>
                {primaryLabel}
                <ArrowRightIcon aria-hidden="true" />
              </Link>
            ) : (
              seeResults && (
                <Link
                  to="/sites/$siteId/scans/$scanId"
                  params={{ siteId, scanId: scan.id }}
                  className={buttonClass('secondary')}
                >
                  {t('check.seeResults')}
                  <ArrowRightIcon aria-hidden="true" />
                </Link>
              )
            )}
            {primaryToFollow && seeResults && (
              <Link
                to="/sites/$siteId/scans/$scanId"
                params={{ siteId, scanId: scan.id }}
                className={buttonClass('secondary')}
              >
                {t('check.seeResults')}
                <ArrowRightIcon aria-hidden="true" />
              </Link>
            )}
            {!primaryToFollow && (
              <Link to="/sites/$siteId/seguimiento" params={{ siteId }} className={buttonClass('secondary')}>
                {primaryLabel}
                <ArrowRightIcon aria-hidden="true" />
              </Link>
            )}
          </div>
        </header>
        {checked && (
          <>
            <p className="check-banner-summary">{checkSummary(t, checked)}</p>
            <p className="text-sm text-ink-muted">
              {t('check.looked', {
                elements: t('check.elementsCount', { count: checked.elements }),
                pages: t('check.pagesCount', { count: checked.pages }),
              })}
            </p>
            <dl className="check-banner-figures">
              <div>
                <dt>{t('follow.check.results.no-detectado')}</dt>
                <dd>{checked.verified}</dd>
              </div>
              <div>
                <dt>{t('follow.check.results.sigue-detectandose')}</dt>
                <dd>{checked.persists}</dd>
              </div>
              <div>
                <dt>{t('check.pending')}</dt>
                <dd>{checked.pending}</dd>
              </div>
            </dl>
            {checked.items.length > 0 && (
              <details className="check-banner-details">
                <summary>{t('check.details')}</summary>
                <CheckItems items={checked.items} />
              </details>
            )}
          </>
        )}
      </aside>
    );
  }

  return (
    <article className="latest-result check-outcome" aria-labelledby={headingId}>
      <header className="latest-head">
        <div className="latest-title">
          <Heading id={headingId}>{t('check.title')}</Heading>
          <p className="check-banner-note">{t('check.titleNote')}</p>
          <p className="latest-meta">
            <span>
              {t('scans.meta', { date: date(scan.createdAt), type: t(`scans.types.${scan.type}`) })}
            </span>
            <StatusTag outcome={scan.outcome} />
          </p>
        </div>
        <div className="check-actions">
          {seeResults && (
            <Link
              to="/sites/$siteId/scans/$scanId"
              params={{ siteId, scanId: scan.id }}
              className={buttonClass('secondary')}
            >
              {t('check.seeResults')}
              <ArrowRightIcon aria-hidden="true" />
            </Link>
          )}
          <Link
            to="/sites/$siteId/seguimiento"
            params={{ siteId }}
            className={buttonClass(primaryToFollow ? 'primary' : 'secondary')}
          >
            {primaryLabel}
            <ArrowRightIcon aria-hidden="true" />
          </Link>
        </div>
      </header>
      <p>{t('check.scope')}</p>
      {checked && (
        <>
          <p className="check-banner-summary">{checkSummary(t, checked)}</p>
          <p>
            {t('check.looked', {
              elements: t('check.elementsCount', { count: checked.elements }),
              pages: t('check.pagesCount', { count: checked.pages }),
            })}
          </p>
          <dl className="summary-figures">
            <div>
              <dt>{t('follow.check.results.no-detectado')}</dt>
              <dd>{checked.verified}</dd>
            </div>
            <div>
              <dt>{t('follow.check.results.sigue-detectandose')}</dt>
              <dd>{checked.persists}</dd>
            </div>
            <div>
              <dt>{t('check.pending')}</dt>
              <dd>{checked.pending}</dd>
            </div>
          </dl>
          <CheckItems items={checked.items} />
        </>
      )}
    </article>
  );
}
